/* eslint-disable import/order */
import { useState, useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "components/button";
import { Input } from "components/input";
import { Spinner } from "components/spinner";
import { avatarIcon } from "components/svgs";
import { Textarea } from "components/textarea";
import { useNotify } from "hooks";
import moment from "moment";
import { useForm } from "react-hook-form";
import { useIntersection, useUpdateEffect } from "react-use";
import { createSchoolReview, fetchReviews } from "services/school";
import { InputWrapper } from "styles/form";
import * as yup from "yup";
import {
   AddReviewContainer,
   NewCommentContainer,
   NewSchoolsCommentsContainer,
} from "./school-comment.style";

const schema = yup.object().shape({
   message: yup.string().required("Message is required"),
   reviewerName: yup.string().required("Name is required"),
});

export interface ISchoolReview {
   message: string;
   reviewerName: string;
   isLoadingReviews: boolean;
}

export const NewSchoolComments = ({ schoolId, setSchoolsReviews, schoolReviews, commentRef }) => {
   const [reviewLimit, _setReviewLimit] = useState(3);
   const [isLoadingReviews, setIsLoadingReviews] = useState(false);

   const intersection = useIntersection(commentRef, {
      rootMargin: "0px",
      threshold: 1,
   });

   const getReviews = useCallback(async () => {
      try {
         setIsLoadingReviews(true);
         const response = await fetchReviews(schoolId as string);
         setSchoolsReviews(response.data.data.reviews);
      } finally {
         setIsLoadingReviews(false);
      }
   }, [schoolId]);

   useUpdateEffect(() => {
      if (intersection && intersection.intersectionRatio >= 1) {
         getReviews();
      }
   }, [getReviews, intersection?.intersectionRatio]);

   return (
      <>
         <NewSchoolsCommentsContainer ref={commentRef}>
            {isLoadingReviews ? (
               <Spinner />
            ) : (
               <div>
                  {schoolReviews?.length ? (
                     <>
                        <h4 className="title-count">
                           {schoolReviews?.length}{" "}
                           {schoolReviews?.length === 1 ? "Review" : "Reviews"}
                        </h4>
                        {schoolReviews
                           ?.slice(0, reviewLimit)
                           .map((review: Record<string, string>) => (
                              <NewComment key={review.id} review={review} />
                           ))}
                     </>
                  ) : (
                     <p style={{ marginBottom: "20px" }}>No Reviews Available</p>
                  )}
               </div>
            )}
         </NewSchoolsCommentsContainer>
      </>
   );
};

export const NewComment = ({ review }) => {
   return (
      <NewCommentContainer>
         <div className="comment-meta">
            <div className="comment-meta_user">
               <div>{avatarIcon}</div>
               <p>{review.reviewerName}</p>
            </div>
            <div className="comment-meta_date">
               <span className="date-text">{moment(review.createdAt).format("DD MMM YYYY")}</span>
            </div>
         </div>
         <p className="comment-description">{review.message}</p>
      </NewCommentContainer>
   );
};

export const AddNewReview = ({ schoolId, setSchoolsReviews, commentRef, name }) => {
   const [isLoading, setIsLoading] = useState(false);
   const { notify } = useNotify();
   const {
      handleSubmit,
      register,
      reset,
      formState: { errors, isValid },
   } = useForm<ISchoolReview>({
      resolver: yupResolver(schema),
      mode: "onChange",
   });

   const getReviewsWithLoadingState = async () => {
      try {
         const response = await fetchReviews(schoolId as string);
         setSchoolsReviews(response.data.data.reviews);
      } catch (err) {
         console.log(err);
      }
   };

   async function onSubmit(values: ISchoolReview): Promise<void> {
      const payload = { ...values, subject: "subject" };
      try {
         setIsLoading(true);
         const result: any = await createSchoolReview(schoolId as string, payload);
         getReviewsWithLoadingState();
         const { data } = result;
         notify({
            message: data.message,
            variant: "success",
         });
         commentRef?.current.scrollIntoView();
         reset();
      } catch (error: any) {
         setIsLoading(false);
         notify({
            message: error?.result?.message || error?.result?.custom_message,
            variant: "error",
         });
      } finally {
         setIsLoading(false);
      }
   }
   return (
      <AddReviewContainer>
         <h3 className="review-title">Review {name} </h3>
         <form onSubmit={handleSubmit(onSubmit)}>
            <InputWrapper>
               <Input
                  label="Full Name"
                  labelColor="#A5ADC0"
                  placeholder="Full name"
                  id="reviewerName"
                  type="text"
                  style={{ borderRadius: "4px" }}
                  {...register("reviewerName")}
                  error={errors.reviewerName?.message}
               />
            </InputWrapper>

            <InputWrapper>
               <Textarea
                  labelColor="#A5ADC0"
                  placeholder="Comment here..."
                  label="Message"
                  id="message"
                  style={{ borderRadius: "4px" }}
                  {...register("message")}
                  error={errors.message?.message}
               />
            </InputWrapper>
            <Button
               disabled={!isValid || isLoading}
               type="submit"
               variant="primary"
               isLoading={isLoading}
               style={{ borderRadius: "8px" }}
            >
               Submit
            </Button>
         </form>
      </AddReviewContainer>
   );
};
